import {Box} from "@mui/material";
import {alpha, type Theme} from "@mui/material/styles";
import type {EditorView} from "@tiptap/pm/view";
import {EditorContent, useEditor} from "@tiptap/react";
import {useEffect, useMemo, useRef, useState} from "react";
import type {VariableCatalogue} from "../newVariablePicker";
import {VariableBubbleMenu} from "../newVariablePicker/chip";
import {variableEditorSx, variableTokensToHtml} from "../newVariablePicker/editor";
import RichTextToolbar from "./parts/RichTextToolbar";
import {richTextExtensions} from "./parts/richTextExtensions";
import {useSetRichTextActiveEditor} from "./RichTextActiveEditor";

type TextSize = 'body' | 'title' | 'heading';

export interface RichTextFieldProps {
    /** Markdown, as stored. */
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    /** One paragraph, for titles: Enter does nothing, pasted line breaks become spaces, no lists. */
    singleLine?: boolean;
    /** Left, center and right, per paragraph. */
    alignment?: boolean;
    /** The emoji button, and emoji suggestions after typing `:`. */
    emoji?: boolean;
    /** The variable button, the `/` menu and the chips. Without it, a stored `{{…}}` is plain text. */
    variables?: VariableCatalogue;
    /** How tall the empty field is, in lines. Ignored on one line. */
    minRows?: number;
    /** `title` and `heading`: a question title and a section heading on the survey canvas, near the guest's sizes. */
    textSize?: TextSize;
    /**
     * A focus request: the field takes focus, cursor at the end, whenever this becomes a number it has not
     * served yet, on mount too. The owner clears it once spent, so a later remount does not take focus again.
     */
    focusKey?: number;
}

const EMOJI_FONTS = ['"Apple Color Emoji"', '"Segoe UI Emoji"', '"Noto Color Emoji"'];

/** MUI's outlined input border at rest, so the field sits among the app's other inputs as one of them. */
const restingBorder = (theme: Theme) =>
    theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)';

const textStyle = (theme: Theme, size: TextSize) => {
    // The survey canvas's sizes, each a little under the 24px title and 28px heading guests see.
    if (size === 'title') return {fontSize: 22, fontWeight: 600, lineHeight: 1.3};
    if (size === 'heading') return {fontSize: 26, fontWeight: 600, lineHeight: 1.3};
    return {fontSize: theme.typography.body1.fontSize, fontWeight: 400, lineHeight: 1.5};
};

const editorContent = (markdown: string, variables?: VariableCatalogue) =>
    variables ? variableTokensToHtml(markdown, variables.variables) : markdown;

/** Pasted lines join with a space, the way a single-line input takes them. */
const pasteOnOneLine = (view: EditorView, event: ClipboardEvent) => {
    const text = event.clipboardData?.getData('text/plain');
    if (!text || !/[\r\n]/.test(text)) return false;

    event.preventDefault();
    view.dispatch(view.state.tr.insertText(text.replace(/\s*[\r\n]+\s*/g, ' ')));
    return true;
};

/**
 * The app's rich text field: markdown in, markdown out. Bold, italic, underline and links always;
 * lists on several lines; alignment, emoji and variables only where the host switches them on.
 */
export default function RichTextField({
    value,
    onChange,
    placeholder = '',
    disabled = false,
    singleLine = false,
    alignment = false,
    emoji = false,
    variables,
    minRows = 3,
    textSize = 'body',
    focusKey,
}: RichTextFieldProps) {
    // A side panel that follows the field being edited, when the page has one.
    const setActiveEditor = useSetRichTextActiveEditor();

    /*
        What this field wrote last: that value coming back in is no reason to reload the text. Any other value is
        another document, so the editor is built again from it, with its own undo history: the survey editor reuses
        one field for every question and language, and undo must never bring the previous text back.
     */
    const emittedRef = useRef(value);
    const documentRef = useRef(0);
    if (value !== emittedRef.current) {
        emittedRef.current = value;
        documentRef.current += 1;
    }
    const documentKey = documentRef.current;

    const latestRef = useRef({value, onChange, placeholder, setActiveEditor});
    latestRef.current = {value, onChange, placeholder, setActiveEditor};

    const withVariables = !!variables;

    const extensions = useMemo(
        () => richTextExtensions({
            singleLine,
            alignment,
            emoji,
            variables,
            placeholder: () => latestRef.current.placeholder,
        }),
        // Later catalogues are swapped in below; rebuilding the editor for one would lose undo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [singleLine, alignment, emoji, withVariables],
    );

    const editor = useEditor({
        extensions,
        content: editorContent(value, variables),
        editable: !disabled,
        shouldRerenderOnTransaction: false,
        onFocus: ({editor: instance}) => latestRef.current.setActiveEditor?.(instance),
        onUpdate: ({editor: instance}) => {
            const markdown = instance.storage.markdown.getMarkdown();
            const next = markdown.trim() ? markdown : '';
            if (next === latestRef.current.value) return;

            emittedRef.current = next;
            latestRef.current.onChange(next);
        },
        editorProps: {
            attributes: {
                role: 'textbox',
                'aria-multiline': String(!singleLine),
                ...(placeholder ? {'aria-label': placeholder} : {}),
            },
            ...(singleLine ? {handlePaste: pasteOnOneLine} : {}),
        },
    }, [extensions, documentKey]);

    // Effects skip a destroyed editor: a hot reload re-runs every effect with the instance being replaced.
    useEffect(() => {
        if (!editor || editor.isDestroyed || !variables) return;
        editor.commands.setVariableCatalogue(variables);
    }, [editor, variables]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        // Without an update: a field only shown must not write its text back reformatted.
        editor.setEditable(!disabled, false);
    }, [editor, disabled]);

    // The cursor lands at the end of the text the field shows: a new value has built its editor by now.
    const servedFocusKeyRef = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (focusKey === undefined || focusKey === servedFocusKeyRef.current) return;
        if (!editor || editor.isDestroyed) return;

        servedFocusKeyRef.current = focusKey;
        editor.commands.focus('end');
    }, [editor, focusKey]);

    // A field that goes away, or is built again, is no longer the one the side panel follows.
    useEffect(() => () => {
        setActiveEditor?.(current => (current === editor ? null : current));
    }, [editor, setActiveEditor]);

    /*
        Being edited: focus is in the text, the toolbar or one of its popups. The popups are portaled, but React
        hands their focus events up the component tree to the frame. Leaving waits a tick, so focus moving from
        the text into a popup does not count as leaving, and the frame keeps its focus border meanwhile.
     */
    const [editing, setEditing] = useState(false);
    const leaveTimerRef = useRef<number | undefined>(undefined);
    useEffect(() => () => window.clearTimeout(leaveTimerRef.current), []);

    const enter = () => {
        window.clearTimeout(leaveTimerRef.current);
        setEditing(true);
    };
    const leave = () => {
        leaveTimerRef.current = window.setTimeout(() => setEditing(false));
    };

    return (
        <Box
            onFocus={enter}
            onBlur={leave}
            data-editing={editing}
            sx={theme => {
                const text = textStyle(theme, textSize);

                return {
                    // Outlined like every input (Simon, 2026-10-07): grey at rest, darker on hover, 2px primary
                    // while editing, and the theme's disabled look.
                    borderRadius: 1,
                    border: '1px solid',
                    ...(disabled
                        ? {
                            borderColor: theme.palette.action.disabled,
                            bgcolor: theme.palette.action.disabledBackground,
                            color: theme.palette.text.disabled,
                            cursor: 'not-allowed',
                        }
                        : {
                            borderColor: restingBorder(theme),
                            '&:hover': {borderColor: theme.palette.text.primary},
                            // The second pixel goes inside the border, so the text does not move.
                            '&:focus-within, &[data-editing="true"]': {
                                borderColor: theme.palette.primary.main,
                                boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
                            },
                        }),
                    '& .ProseMirror': {
                        px: 1.75,
                        py: 1,
                        outline: 'none',
                        fontFamily: [theme.typography.fontFamily, ...EMOJI_FONTS].join(', '),
                        ...text,
                        ...(singleLine
                            // A small outlined input's 40px, border included; a long title still wraps and grows.
                            ? {minHeight: 38, display: 'flex', flexDirection: 'column', justifyContent: 'center'}
                            : {minHeight: `calc(${minRows * text.lineHeight}em + ${theme.spacing(2)})`}),
                        '& p': {m: 0},
                        '& p + p': {mt: 1},
                        '& ul, & ol': {m: 0, pl: 3},
                        '& a': {color: 'primary.main'},
                        '& ::selection': {bgcolor: alpha(theme.palette.primary.main, 0.18)},
                    },
                    // MUI's placeholder: the text colour at 42%.
                    '& .ProseMirror p.is-editor-empty:first-of-type::before': {
                        content: 'attr(data-placeholder)',
                        float: 'left',
                        height: 0,
                        pointerEvents: 'none',
                        color: theme.palette.text.primary,
                        opacity: 0.42,
                    },
                    ...variableEditorSx(theme),
                };
            }}
        >
            {/*
                Keyed by the editor: after a rebuild, tiptap's `useEditorState` still hands its selector the
                destroyed editor for a render, and asking that one `can()` throws.
             */}
            {!disabled && !!editor && (
                <RichTextToolbar
                    key={`toolbar-${editor.instanceId}`}
                    editor={editor}
                    lists={!singleLine}
                    alignment={alignment}
                    emoji={emoji}
                />
            )}
            <EditorContent editor={editor}/>
            {!disabled && !!editor && withVariables && (
                <VariableBubbleMenu key={`bubble-${editor.instanceId}`} editor={editor}/>
            )}
        </Box>
    );
}
